import jwtDecode from 'jwt-decode'
import { isServer } from '.'
import { JWTData } from '../../types'

export const isTokenExpired = (data: JWTData) => {
  //  exp == Unix timestamp
  const { exp } = data
  const nowTimestamp = Math.floor(Date.now() / 1000)
  return Number(new Date(exp)) - nowTimestamp <= 0
}

export interface DecodedTokenReturn {
  isExpired: boolean
  data: JWTData
}

export const processToken = (token: string): DecodedTokenReturn => {
  const decodedToken = jwtDecode<JWTData>(token)
  return { isExpired: isTokenExpired(decodedToken), data: decodedToken }
}

export const signupUserTrial = async (
  data: {
    email: string
    businessRole: string
    companyName?: string
    message?: string
    acceptNewsletter: boolean
  },
  product: {
    product: string
    platform: string
    os: string
  },
): Promise<{
  data: { id?: string; fullName: string; token: string }
}> => {
  let apiUrl = `${process.env.GATSBY_PDFTOOLS_API_URL}/Users/RegisterTrial?product=${product.product}`

  if (product.platform) {
    apiUrl = `${apiUrl}&platform=${product.platform.replace(/ /g, '')}`
  }

  if (product.os) {
    apiUrl = `${apiUrl}&os=${product.os.replace(/ /g, '')}`
  }

  console.log('Stripped URL: ', apiUrl)

  apiUrl = encodeURI(apiUrl)

  console.log('Encoded URL: ', apiUrl)

  const res = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      email: data.email,
      newsletter: data.acceptNewsletter,
      // TODO (LANGUAGES): Add current language
      language: 'en',
      // TODO: Send these fields
      message: data.message,
      productVariant: product.platform,
      productName: product.product,
      operatingSystem: product.os,
      businessRole: data.businessRole,
      customer: {
        name: 'Not provided',
      },
    }),
  })

  if (res.status === 400) {
    throw new Error('ERROR_MISSING_DATA')
  }

  if (res.status >= 500) {
    throw new Error('Something went wrong')
  }

  const json = await res.json()

  if (json.token && !isServer()) {
    window.localStorage.setItem('token', json.token)
    window.localStorage.setItem('trialProductCode', product.product)
  }

  return { data: json }
}

export const loginUser = async (data: { email: string; password: string }) => {
  const apiUrl = `${process.env.GATSBY_PDFTOOLS_API_URL}/Users/Authenticate`

  const res = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      email: data.email,
      password: data.password,
    }),
  })

  if (res.status === 400) {
    throw new Error('ERROR_INVALID_CREDENTIALS')
  }

  if (res.status === 403) {
    throw new Error('ERROR_ACCOUNT_DEACTIVATED')
  }

  if (res.status !== 200) {
    throw new Error('ERROR_SOMETHING_WENT_WRONG')
  }

  const json: {
    id?: string
    fullName: string
    token: string
  } = await res.json()

  return { data: json }
}

export const resetPassword = async (email: string) => {
  const apiUrl = `${process.env.GATSBY_PDFTOOLS_API_URL}/Users/ResetPassword`

  const res = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      email,
    }),
  })

  if (res.status === 400) {
    throw new Error('ERROR_INVALID_CREDENTIALS')
  }

  if (res.status !== 200 && res.status !== 204) {
    throw new Error('ERROR_SOMETHING_WENT_WRONG')
  }

  return { data: true }
}

export const getDownloadsForProduct = async (product: string, code: string) => {
  const token = isServer() ? '' : window.localStorage.getItem('token')
  const res = await fetch(`${process.env.GATSBY_PDFTOOLS_API_URL}/Kits/EvalDownloadData/${product}/${code}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (res.status === 404) {
    throw new Error('ERROR_NO_DOWNLOADS')
  }

  if (res.status === 410) {
    throw new Error('ERROR_DOWNLOAD_EXPIRED')
  }

  if (res.status !== 200) {
    throw new Error('ERROR_SOMETHING_WENT_WRONG')
  }

  const json = await res.json()

  return json
}

export const getDownloadLinkURL = (productCode: string) => {
  if (productCode.includes('PdfToolsSDK_Shell')) {
    if (productCode.includes('linux')) {
      return 'https://pdftools-public-downloads-production.s3.eu-west-1.amazonaws.com/productkits/PDFSHELL/latest/PdfToolsSDK_Shell-latest-linux-x64.zip'
    }
    if (productCode.includes('win')) {
      return 'https://pdftools-public-downloads-production.s3.eu-west-1.amazonaws.com/productkits/PDFSHELL/latest/PdfToolsSDK_Shell-latest-win-x64.zip'
    }
    if (productCode.includes('osx-arm64')) {
      return 'https://pdftools-public-downloads-production.s3.eu-west-1.amazonaws.com/productkits/PDFSHELL/latest/PdfToolsSDK_Shell-latest-osx-arm64.zip'
    }
    if (productCode.includes('osx-x64')) {
      return 'https://pdftools-public-downloads-production.s3.eu-west-1.amazonaws.com/productkits/PDFSHELL/latest/PdfToolsSDK_Shell-latest-osx-x64.zip'
    }
  }
  if (productCode.includes('PdfToolsSDK_C')) {
    return 'https://pdftools-public-downloads-production.s3.eu-west-1.amazonaws.com/productkits/PDFSDK/latest/PdfToolsSDK_C-latest.zip'
  }
  if (productCode.includes('PdfToolsSDK_Java')) {
    return 'https://pdftools-public-downloads-production.s3.eu-west-1.amazonaws.com/productkits/PDFSDK/latest/PdfToolsSDK_Java-latest.zip'
  }
  if (productCode.includes('PdfTools-latest.nupkg')) {
    return 'https://pdftools-public-downloads-production.s3.eu-west-1.amazonaws.com/productkits/PDFSDK/latest/PdfTools-latest.nupkg'
  }

  if (productCode.includes('PdfToolsSDK_glibc2.12')) {
    return 'https://pdftools-public-downloads-production.s3.eu-west-1.amazonaws.com/productkits/PDFSDK/latest/PdfToolsSDK_glibc2.12-latest.zip'
  }

  return 'https://pdftools-public-downloads-production.s3.eu-west-1.amazonaws.com/productkits/PDFSDK/latest/PdfToolsSDK_C-latest.zip'
}

export const getLicenseKey = async (licenseId: string) => {
  const token = !isServer() && window.localStorage.getItem('token')

  const res = await fetch(`${process.env.GATSBY_PDFTOOLS_API_URL}/Kits/Licensekey/${licenseId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (res.status === 404) {
    throw new Error('ERROR_NO_LICENSE')
  }

  if (res.status !== 200) {
    throw new Error('ERROR_SOMETHING_WENT_WRONG')
  }

  const json = await res.json()

  return json as { key: string }
}
